import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Tag,
  Space,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  message,
  Modal,
  Tooltip,
  Badge,
} from "antd";
import {
  EyeOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  CalendarOutlined,
  UserOutlined,
  ReloadOutlined,
  ShoppingOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { API_METHODS } from "../../../constants";
import useApi from "../../../hooks/useApi";

const { RangePicker } = DatePicker;
const { Option } = Select;

function OrdersTable({ onViewDetails, refreshTrigger }) {
  const [filters, setFilters] = useState({
    pageNumber: 1,
    order_status: "all",
    payment_status: "all",
    service_type: "all",
    search_order_number: "",
    search_email: "",
    search_name: "",
    from_date: null,
    to_date: null,
  });

  // API hooks
  const [ordersResponse, getOrders] = useApi(
    "/admin/orders/all",
    null,
    API_METHODS.GET
  );
  const [updateResponse, updateOrder] = useApi();
  const [bulkUpdateResponse, bulkUpdate] = useApi();
  const [modal, contextHolder] = Modal.useModal();

  // Fetch orders
  const fetchOrders = () => {
    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== "all" && value !== "") {
        queryParams.append(key, value);
      }
    });
    getOrders(
      null,
      API_METHODS.GET,
      `/admin/orders/all?${queryParams.toString()}`
    );
  };

  useEffect(() => {
    fetchOrders();
  }, [filters, refreshTrigger]);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value, pageNumber: 1 }));
  };

  const handlePageChange = (page) => {
    setFilters((prev) => ({
      ...prev,
      pageNumber: page,
    }));
  };

  // Handle date range change
  const handleDateRangeChange = (dates) => {
    setFilters((prev) => ({
      ...prev,
      from_date: dates ? dates[0].format("YYYY-MM-DD") : null,
      to_date: dates ? dates[1].format("YYYY-MM-DD") : null,
      pageNumber: 1,
    }));
  };

  // Handle order status update
  const handleStatusUpdate = (orderId, status) => {
    const statusText = status === "in_progress" ? "In Progress" : "Completed";

    modal.confirm({
      title: "Update Order Status",
      content: `Are you sure you want to mark this order as ${statusText}?`,
      onOk: () => {
        updateOrder({ status }, API_METHODS.PUT, `/admin/orders/${orderId}`);
      },
    });
  };

  // Handle bulk update
  const handleBulkUpdate = () => {
    const selectedRows = selectedRowKeys;
    if (selectedRows.length === 0) {
      message.warning("Please select orders to update");
      return;
    }

    modal.confirm({
      title: "Bulk Complete Orders",
      content: (
        <div>
          <p>Mark {selectedRows.length} selected orders as completed?</p>
        </div>
      ),
      onOk: () => {
        bulkUpdate(
          { order_ids: selectedRows },
          API_METHODS.PUT,
          "/admin/orders/bulk-update"
        );
      },
    });
  };

  // Row selection - only allow in_progress orders to be selected
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    getCheckboxProps: (record) => ({
      disabled:
        record.order_status !== "in_progress" &&
        record.order_status !== "confirmed",
    }),
  };

  // Success handlers
  useEffect(() => {
    if (updateResponse.data && !updateResponse.isLoading) {
      message.success("Order status updated successfully");
      fetchOrders();
    }
  }, [updateResponse]);

  useEffect(() => {
    if (bulkUpdateResponse.data && !bulkUpdateResponse.isLoading) {
      const result = bulkUpdateResponse.data;
      message.success(
        `${result.successfully_updated} orders updated successfully`
      );
      if (result.skipped_orders && result.skipped_orders.length > 0) {
        message.info(
          `${result.skipped_orders.length} orders were skipped (not in correct status)`
        );
      }
      setSelectedRowKeys([]);
      fetchOrders();
    }
  }, [bulkUpdateResponse]);

  // Format currency
  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount).toLocaleString("en-IN")}`;
  };

  // Get service count display
  const getServiceCountDisplay = (record) => {
    const serviceCount = record.service_count || 0;
    const uniqueServiceCount = record.unique_service_count || 0;

    if (serviceCount === uniqueServiceCount) {
      return (
        <Tag color="blue" size="small">
          <ShoppingOutlined style={{ marginRight: "4px" }} />
          {serviceCount} Service{serviceCount > 1 ? "s" : ""}
        </Tag>
      );
    } else {
      return (
        <Tooltip
          title={`${serviceCount} total service instances from ${uniqueServiceCount} unique services`}
        >
          <Tag color="purple" size="small">
            <TeamOutlined style={{ marginRight: "4px" }} />
            {serviceCount} Instances
          </Tag>
        </Tooltip>
      );
    }
  };

  // Table columns
  const columns = [
    {
      title: "Order Details",
      key: "order_details",
      width: 200,
      render: (record) => (
        <div>
          <div style={{ marginBottom: "4px" }}>
            <Tag
              color="blue"
              style={{ fontFamily: "monospace", fontSize: "11px" }}
            >
              {record.order_number}
            </Tag>
          </div>
          <div style={{ marginBottom: "4px" }}>
            {getServiceCountDisplay(record)}
          </div>
          <div style={{ fontSize: "11px", color: "#666" }}>
            <CalendarOutlined style={{ marginRight: "4px" }} />
            Created: {new Date(record.created_on).toLocaleDateString("en-IN")}
          </div>
        </div>
      ),
    },
    {
      title: "Customer Details",
      key: "customer",
      width: 180,
      render: (record) => (
        <div>
          <div style={{ fontWeight: "500", marginBottom: "4px" }}>
            {record.customer_name || `${record.first_name || ""} ${record.last_name || ""}`.trim() || "—"}
          </div>
          <div style={{ fontSize: "12px", color: "#666", marginBottom: "2px" }}>
            {record.email}
          </div>
          {record.phone && (
            <div style={{ fontSize: "12px", color: "#666" }}>
              📞 {record.phone}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Amount",
      dataIndex: "total_amount",
      key: "total_amount",
      width: 120,
      render: (amount) => (
        <div style={{ fontWeight: "600", color: "#f97316", fontSize: "16px" }}>
          {formatCurrency(amount)}
        </div>
      ),
      sorter: (a, b) => parseFloat(a.total_amount) - parseFloat(b.total_amount),
    },
    {
      title: "Booking Date",
      dataIndex: "created_on",
      key: "created_on",
      width: 120,
      render: (date) => (
        <div style={{ textAlign: "center" }}>
          <CalendarOutlined style={{ color: "#1890ff", marginBottom: "4px" }} />
          <div style={{ fontSize: "12px", fontWeight: "500" }}>
            {new Date(date).toLocaleDateString("en-IN", {
              day: "numeric",
              month: "short",
              year: "2-digit",
            })}
          </div>
        </div>
      ),
      sorter: (a, b) => new Date(a.created_on) - new Date(b.created_on),
    },
    {
      title: "Puja Date",
      dataIndex: "booking_date",
      key: "booking_date",
      width: 120,
      render: (date) => (
        <div style={{ textAlign: "center" }}>
          <CalendarOutlined style={{ color: "#f97316", marginBottom: "4px" }} />
          <div style={{ fontSize: "12px", fontWeight: "500" }}>
            {new Date(date).toLocaleDateString("en-IN", {
              day: "numeric",
              month: "short",
              year: "2-digit",
            })}
          </div>
        </div>
      ),
      sorter: (a, b) => new Date(a.booking_date) - new Date(b.booking_date),
    },
    {
      title: "Status",
      key: "status",
      width: 140,
      render: (record) => (
        <div>
          <div style={{ marginBottom: "4px" }}>
            <Tag
              color={
                record.order_status === "completed"
                  ? "green"
                  : record.order_status === "in_progress"
                  ? "purple"
                  : record.order_status === "confirmed"
                  ? "cyan"
                  : record.order_status === "payment_completed"
                  ? "blue"
                  : record.order_status === "pending"
                  ? "orange"
                  : "red"
              }
              style={{ fontSize: "11px" }}
            >
              {record.order_status?.toUpperCase().replace("_", " ")}
            </Tag>
          </div>
          <div>
            <Tag
              color={
                record.payment_status === "completed"
                  ? "green"
                  : record.payment_status === "pending"
                  ? "orange"
                  : "red"
              }
              size="small"
              style={{ fontSize: "10px" }}
            >
              {record.payment_status === "completed"
                ? "PAID"
                : record.payment_status?.toUpperCase()}
            </Tag>
          </div>
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 140,
      fixed: "right",
      render: (record) => (
        <Space direction="vertical" size="small">
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => onViewDetails && onViewDetails(record)}
            block
            style={{
              backgroundColor: "#f97316",
              borderColor: "#f97316",
              color: "#fff",
              fontWeight: 500,
            }}
          >
            View Details
          </Button>

          {record.order_status === "confirmed" && (
            <Space direction="vertical" size="small" style={{ width: "100%" }}>
              <Button
                type="primary"
                size="small"
                icon={<CheckCircleOutlined />}
                onClick={() => handleStatusUpdate(record.id, "completed")}
                style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
                block
              >
                Complete
              </Button>
            </Space>
          )}
          {record.order_status === "in_progress" && (
            <Button
              type="default"
              size="small"
              icon={<CheckCircleOutlined />}
              onClick={() => handleStatusUpdate(record.id, "completed")}
              style={{ color: "#52c41a", borderColor: "#52c41a" }}
              block
            >
              Complete
            </Button>
          )}
        </Space>
      ),
    },
  ];

  // Calculate enhanced statistics
  const enhancedStats = ordersResponse.data
    ? {
        total_orders: ordersResponse.pagination?.count || 0,
        total_services: ordersResponse.data.reduce(
          (sum, order) => sum + parseInt(order.service_count || 0),
          0
        ),
        confirmed_orders: ordersResponse.data.filter(
          (o) => o.order_status === "confirmed"
        ).length,
        in_progress_orders: ordersResponse.data.filter(
          (o) => o.order_status === "in_progress"
        ).length,
        completed_orders: ordersResponse.data.filter(
          (o) => o.order_status === "completed"
        ).length,
        total_value: ordersResponse.data.reduce(
          (sum, o) => sum + parseFloat(o.total_amount || 0),
          0
        ),
        pending_payment: ordersResponse.data.filter(
          (o) => o.payment_status === "pending"
        ).length,
      }
    : {};

  return (
    <div style={{ padding: "24px" }}>
      {contextHolder}
      <Card
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            <UserOutlined style={{ marginRight: "8px", color: "#f97316" }} />
            Order Management System
          </div>
        }
        extra={
          <Space>
            {selectedRowKeys.length > 0 && (
              <Badge count={selectedRowKeys.length} offset={[-5, 5]}>
                <Button
                  type="primary"
                  onClick={handleBulkUpdate}
                  loading={bulkUpdateResponse.isLoading}
                  style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
                >
                  Complete Selected
                </Button>
              </Badge>
            )}
            <Button icon={<ReloadOutlined />} onClick={fetchOrders}>
              Refresh
            </Button>
          </Space>
        }
      >
        {/* Enhanced Filters */}
        <div
          style={{
            marginBottom: "16px",
            padding: "16px",
            backgroundColor: "#fafafa",
            borderRadius: "6px",
          }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Input
                placeholder="Search Order Number"
                prefix={<SearchOutlined />}
                value={filters.search_order_number}
                onChange={(e) =>
                  handleFilterChange("search_order_number", e.target.value)
                }
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Input
                placeholder="Search Email"
                prefix={<SearchOutlined />}
                value={filters.search_email}
                onChange={(e) =>
                  handleFilterChange("search_email", e.target.value)
                }
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Input
                placeholder="Search Customer Name"
                prefix={<UserOutlined />}
                value={filters.search_name}
                onChange={(e) =>
                  handleFilterChange("search_name", e.target.value)
                }
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Order Status"
                value={filters.order_status}
                onChange={(value) => handleFilterChange("order_status", value)}
                style={{ width: "100%" }}
              >
                <Option value="all">All Status</Option>
                <Option value="pending">Pending</Option>
                <Option value="payment_completed">Payment Completed</Option>
                <Option value="confirmed">Confirmed</Option>
                <Option value="in_progress">In Progress</Option>
                <Option value="completed">Completed</Option>
                <Option value="payment_failed">Payment Failed</Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Payment Status"
                value={filters.payment_status}
                onChange={(value) =>
                  handleFilterChange("payment_status", value)
                }
                style={{ width: "100%" }}
              >
                <Option value="all">All Payments</Option>
                <Option value="pending">Pending</Option>
                <Option value="completed">Completed</Option>
                <Option value="failed">Failed</Option>
                <Option value="refunded">Refunded</Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <RangePicker
                placeholder={["From Date", "To Date"]}
                onChange={handleDateRangeChange}
                style={{ width: "100%" }}
              />
            </Col>
          </Row>
        </div>

        {/* Enhanced Statistics Dashboard */}
        {ordersResponse.data && (
          <Row gutter={16} style={{ marginBottom: "20px" }}>
            <Col xs={12} sm={8} md={4}>
              <Card
                size="small"
                style={{ textAlign: "center", backgroundColor: "#f0f9ff" }}
              >
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: "bold",
                    color: "#f97316",
                  }}
                >
                  {enhancedStats.total_orders}
                </div>
                <div style={{ color: "#666", fontSize: "12px" }}>
                  Total Orders
                </div>
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card
                size="small"
                style={{ textAlign: "center", backgroundColor: "#f0fff4" }}
              >
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: "bold",
                    color: "#1890ff",
                  }}
                >
                  {enhancedStats.total_services}
                </div>
                <div style={{ color: "#666", fontSize: "12px" }}>
                  Total Services
                </div>
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card
                size="small"
                style={{ textAlign: "center", backgroundColor: "#fff7e6" }}
              >
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: "bold",
                    color: "#13c2c2",
                  }}
                >
                  {enhancedStats.confirmed_orders}
                </div>
                <div style={{ color: "#666", fontSize: "12px" }}>Confirmed</div>
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card
                size="small"
                style={{ textAlign: "center", backgroundColor: "#f9f0ff" }}
              >
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: "bold",
                    color: "#722ed1",
                  }}
                >
                  {enhancedStats.in_progress_orders}
                </div>
                <div style={{ color: "#666", fontSize: "12px" }}>
                  In Progress
                </div>
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card
                size="small"
                style={{ textAlign: "center", backgroundColor: "#f6ffed" }}
              >
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: "bold",
                    color: "#52c41a",
                  }}
                >
                  {enhancedStats.completed_orders}
                </div>
                <div style={{ color: "#666", fontSize: "12px" }}>Completed</div>
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card
                size="small"
                style={{ textAlign: "center", backgroundColor: "#fff1f0" }}
              >
                <div
                  style={{
                    fontSize: "18px",
                    fontWeight: "bold",
                    color: "#faad14",
                  }}
                >
                  {formatCurrency(enhancedStats.total_value)}
                </div>
                <div style={{ color: "#666", fontSize: "12px" }}>
                  Total Value
                </div>
              </Card>
            </Col>
          </Row>
        )}

        {/* Quick Action Alerts */}
        {enhancedStats.confirmed_orders > 0 && (
          <div style={{ marginBottom: "16px" }}>
            <Card
              size="small"
              style={{
                backgroundColor: "#e6f7ff",
                border: "1px solid #91d5ff",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <CheckCircleOutlined
                    style={{ color: "#52c41a", marginRight: "8px" }}
                  />
                  <span style={{ fontWeight: "500" }}>
                    {enhancedStats.confirmed_orders} orders can be completed
                    directly
                  </span>
                </div>
                <Button
                  type="link"
                  onClick={() =>
                    handleFilterChange("order_status", "confirmed")
                  }
                  style={{ padding: 0 }}
                >
                  View Confirmed Orders →
                </Button>
              </div>
            </Card>
          </div>
        )}
        {enhancedStats.in_progress_orders > 0 && (
          <div style={{ marginBottom: "16px" }}>
            <Card
              size="small"
              style={{
                backgroundColor: "#f9f0ff",
                border: "1px solid #d3adf7",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <CheckCircleOutlined
                    style={{ color: "#722ed1", marginRight: "8px" }}
                  />
                  <span style={{ fontWeight: "500" }}>
                    {enhancedStats.in_progress_orders} orders are in progress
                    and can be completed
                  </span>
                </div>
                <Button
                  type="link"
                  onClick={() =>
                    handleFilterChange("order_status", "in_progress")
                  }
                  style={{ padding: 0 }}
                >
                  View In Progress Orders →
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Main Orders Table */}
        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={ordersResponse.data || []}
          loading={ordersResponse.isLoading}
          rowKey="id"
          pagination={{
            current: filters.pageNumber,
            total: ordersResponse.pagination?.count || 0,
            pageSize: 10,
            showSizeChanger: false,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} orders`,
            onChange: (page) => {
              handlePageChange(page);
              // Smooth scroll to top of table
              const table = document.querySelector(".ant-table");
              if (table) {
                table.scrollIntoView({ behavior: "smooth", block: "start" });
              } else {
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            },
          }}
          scroll={{ x: 1000 }}
          size="small"
          rowClassName={(record) => {
            // Add visual indicators for different order statuses
            if (record.order_status === "confirmed") {
              return "confirmed-order-row";
            } else if (record.order_status === "in_progress") {
              return "in-progress-order-row";
            } else if (record.order_status === "completed") {
              return "completed-order-row";
            }
            return "";
          }}
        />

        {/* Help Section */}
        <div
          style={{
            marginTop: "24px",
            padding: "16px",
            backgroundColor: "#f6ffed",
            borderRadius: "6px",
            border: "1px solid #b7eb8f",
          }}
        >
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <div style={{ marginBottom: "8px" }}>
                <Tag color="cyan">CONFIRMED</Tag>
                <span style={{ fontSize: "12px", color: "#666" }}>
                  Ready to start progress
                </span>
              </div>
            </Col>
            <Col xs={24} md={8}>
              <div style={{ marginBottom: "8px" }}>
                <Tag color="purple">IN PROGRESS</Tag>
                <span style={{ fontSize: "12px", color: "#666" }}>
                  Services are being performed
                </span>
              </div>
            </Col>
            <Col xs={24} md={8}>
              <div style={{ marginBottom: "8px" }}>
                <Tag color="green">COMPLETED</Tag>
                <span style={{ fontSize: "12px", color: "#666" }}>
                  All services finished
                </span>
              </div>
            </Col>
          </Row>
          <div style={{ marginTop: "12px", fontSize: "12px", color: "#666" }}>
            💡 <strong>Tip:</strong> Use bulk actions to complete multiple
            orders at once. Select orders in "In Progress" status and click
            "Complete Selected".
          </div>
        </div>
      </Card>

      {/* Custom CSS for row highlighting */}
      <style jsx>{`
        .confirmed-order-row {
          background-color: #e6f7ff !important;
        }
        .confirmed-order-row:hover {
          background-color: #bae7ff !important;
        }
        .in-progress-order-row {
          background-color: #f9f0ff !important;
        }
        .in-progress-order-row:hover {
          background-color: #efdbff !important;
        }
        .completed-order-row {
          background-color: #f6ffed !important;
        }
        .completed-order-row:hover {
          background-color: #d9f7be !important;
        }
      `}</style>
    </div>
  );
}

export default OrdersTable;
